/* eslint-disable no-unsafe-optional-chaining */
import i18n from '@_plugins/i18n';
import { container } from '@_plugins/ioc';
import LaravelAPI from '@data/api/laravel.api';
import {
  toTrainingCourseContract,
  toTrainingCourseContractIncluded,
} from '@data/dto/training-course-contract.dto';
import {
  TrainingCourseContract,
  TrainingCourseContractHttp,
  TrainingCourseContractRawIncluded,
  TrainingCourseContractWizard,
} from '@data/models/training-course-contract.interface';
import ErrorService from 'core/error.service';
import { injectable } from 'inversify';

@injectable()
export default class TrainingCourseContractService {
  private API = container.get<LaravelAPI>(LaravelAPI);
  private readonly trainingCourseContractEndpoint =
    '/training-course-contracts';
  private errorService = container.get<ErrorService>(ErrorService);

  public async upsertTrainingCourseContract(
    { id, ...trainingCourseContractWizard }: TrainingCourseContractWizard,
    isDraft = true,
  ): Promise<TrainingCourseContract | void> {
    if (!id) {
      const { data, error } = await this.API?.post<
        { data: Omit<TrainingCourseContractWizard, 'id'> },
        {
          data: TrainingCourseContractHttp;
          included: TrainingCourseContractRawIncluded;
        }
      >(
        `${this.trainingCourseContractEndpoint}${(isDraft && '/draft') || ''}`.trim() +
          `?include=training_course,training_course_group,qualification,sector,candidates,training_course_estimator,training_course_estimator.device_type,training_course_estimator.educational_path,training_course_estimator.educational_path.training_course_trainings`,
        {
          data: trainingCourseContractWizard,
        },
      );

      if (error) {
        this.errorService.handleHttpError(error);
        return;
      }

      const included = toTrainingCourseContractIncluded(data?.included || []);

      return toTrainingCourseContract(data?.data ?? {}, included);
    }
    const { data, error } = await this.API?.patch<
      { data: TrainingCourseContractWizard },
      {
        data: TrainingCourseContractHttp;
        included: TrainingCourseContractRawIncluded;
      }
    >(
      `${this.trainingCourseContractEndpoint}/${id}${(isDraft && '/draft') || ''}?include=training_course,training_course_group,qualification,sector,candidates,training_course_estimator,training_course_estimator.device_type,training_course_estimator.educational_path,training_course_estimator.educational_path.training_course_trainings`,
      {
        data: { ...trainingCourseContractWizard, id },
      },
    );

    if (error) {
      this.errorService.handleHttpError(error);
      return;
    }

    const included = toTrainingCourseContractIncluded(data?.included || []);

    return toTrainingCourseContract(data?.data || {}, included);
  }

  public async sendToLucie({
    id,
    ...trainingCourseContractWizard
  }: TrainingCourseContractWizard): Promise<TrainingCourseContract | void> {
    if (!id) {
      this.errorService.handleError({
        title: i18n.t('error.sending'),
        message: i18n.t('error.id.missing'),
      });
      return;
    }
    const { data, error } = await this.API?.patch<
      { data: TrainingCourseContractWizard },
      {
        data: TrainingCourseContractHttp;
        included: TrainingCourseContractRawIncluded;
      }
    >(
      `${this.trainingCourseContractEndpoint}/${id}/send-to-lucie?include=training_course,training_course_group,qualification,sector,candidates,training_course_estimator,training_course_estimator.device_type,training_course_estimator.educational_path,training_course_estimator.educational_path.training_course_trainings`,
      {
        data: { ...trainingCourseContractWizard, id },
      },
    );

    if (error) {
      this.errorService.handleHttpError(error);
      return;
    }

    const included = toTrainingCourseContractIncluded(data?.included || []);

    return toTrainingCourseContract(data?.data ?? {}, included);
  }

  public async deleteTrainingCourseContract(
    id: string,
  ): Promise<TrainingCourseContract | void> {
    if (!id) {
      this.errorService.handleError({
        title: i18n.t('error.deleting'),
        message: i18n.t('error.id.missing'),
      });
      return;
    }

    const { data, error } = await this.API?.delete<{
      data: TrainingCourseContractHttp;
    }>(`${this.trainingCourseContractEndpoint}/${id}`);

    if (error) {
      this.errorService.handleHttpError(error);
      return;
    }

    return toTrainingCourseContract(data?.data || {}, {});
  }
}
